import type { AsyncOperationType } from '../dto/asyncOperationType';
import type { UIOnlySections } from '../types/UIOnlySections';

export interface Permission {
  permission: string;
  defaultValue: boolean;
}

export type PermissionId = (
  'userManagement/tenantInfo' |
  'userManagement/tenantSupport' |
  'userManagement/isTenantAdmin' |
  'userManagement/isSuperAdmin' |
  'allTenants/billing' |
  'allTenants/write' |
  'allTenants/tunnel' |
  'allTenants/kelda' |
  'folder/create' |
  'tenantSystem/read' |
  'tenantSystem/write'
);

function getTypedPermission(id: PermissionId, defaultValue: boolean) {
  return getPermission(id, defaultValue);
}

const getPermission = (name: string, defaultValue: boolean): Permission => ({
  permission: name,
  defaultValue: defaultValue,
});

const defaultDeniedAnalyticsOps: AsyncOperationType[] = [
  'transcoding',
  'transcription',
  'personDetection',
  'celebrityDetection',
  'labelDetection',
  'textDetection',
  'subclip',
];

const defaultAllowedAnalyticsOps: AsyncOperationType[] = [
  'translation',
  'sceneDetection',
];

function getAnalyticPermissions(types: AsyncOperationType[], defaultValue: boolean) {
  return types.map(op => {
    const entry: [string, Permission] = [op, getPermission('analytics/' + op, defaultValue)];
    return entry;
  });
}

export const AnalyticsPermissions = Object.fromEntries<Permission>([
  ...getAnalyticPermissions(defaultDeniedAnalyticsOps, false),
  ...getAnalyticPermissions(defaultAllowedAnalyticsOps, true)
]);

export const GeneralPermissions = {
  bookmarks: getPermission('general/bookmarks', false),
  playout: getPermission('general/playout', false),
  playoutDebug: getPermission('general/playoutDebug', false),
  setVisibleToSelf: getPermission('general/setVisibleToSelf', true),
  setVisibleToGroups: getPermission('general/setVisibleToGroups', false),
  setVisibleToUsers: getPermission('general/setVisibleToUsers', false),
  bulkMetadataUpdate: getPermission('general/bulkMetadataUpdate', false),
  mimirCutter: getPermission('general/mimirCutter', false),
  apiKeys: getPermission('general/apiKeys', false),
  getGroups: getPermission('general/getGroups', false),
};

export const ClassifiedUserManagementPermissions = {
  superAdmin: getTypedPermission('userManagement/isSuperAdmin', false),
  tenantAdmin: getTypedPermission('userManagement/isTenantAdmin', false),
  supportTeam: getPermission('userManagement/isSupportTeam', false),
};

// Only super admins from 'mimiradmin' tenant are allowed to add these permissions to a group
export const ClassifiedAllTenantsPermissions = {
  billing: getTypedPermission('allTenants/billing', false),
  write: getTypedPermission('allTenants/write', false),
  tunnel: getTypedPermission('allTenants/tunnel', false),
  kelda: getTypedPermission('allTenants/kelda', false),
};

export const UserManagementPermissions = {
  tenantInfo: getTypedPermission('userManagement/tenantInfo', false),
  tenantSupport: getTypedPermission('userManagement/tenantSupport', false),
  tenantSystemRead: getTypedPermission('tenantSystem/read', false),
  tenantSystemWrite: getTypedPermission('tenantSystem/write', false),
};

export const SystemActivityPermissions = {
  download: getPermission('systemActivity/download', true),
  processor: getPermission('systemActivity/processor', true),
  upload: getPermission('systemActivity/upload', true),
};

export const UIOnlyPermissions: Record<UIOnlySections, Permission> = {
  'metadata': getPermission('tab/metadata', true),
  'logging': getPermission('tab/logging', true),
  'relations': getPermission('tab/relations', true),
  'analytics': getPermission('tab/analytics', true),
  'comments': getPermission('tab/comments', true),
  'map': getPermission('tab/map', true),
  'attachments': getPermission('tab/attachments', true),
  'cutter': getPermission('tab/cutter', false),
  'shares': getPermission('tab/shares', true),
  'plugin': getPermission('tab/plugin', true),
  'persons': getPermission('tab/persons', true),
  'usageRules': getPermission('tab/usageRules', true),
  'translation': getPermission('tab/translation', true),
  'transcript': getPermission('tab/transcript', true),
  'labels': getPermission('tab/labels', true),
  'searchFilter': getPermission('sidebar/searchFilter', true),
};

export const ItemPermissions = {
  create: getPermission('item/create', false),
  upload: getPermission('item/upload', false),
  exportSequence: getPermission('item/exportSequence', false),
  dependenciesOnExportSequence: getPermission('item/dependenciesOnExportSequence', false),
  delete: getPermission('item/delete', false),
  recoverDeleted: getPermission('item/recoverDeleted', true),
  update: getPermission('item/update', true),
  updateMetadata: getPermission('item/updateMetadata', true),
  updateOwnMetadata: getPermission('item/updateOwnMetadata', false),
  copyMedia: getPermission('item/copyMedia', false),
  updateTranscript: getPermission('item/updateTranscript', true),
  createVirtualClip: getPermission('item/createVirtualClip', false),
  archive: getPermission('item/archive', false),
  unarchive: getPermission('item/unarchive', false),
  relation: getPermission('item/relation', false),
  version: getPermission('item/version', false),
  promote: getPermission('item/promote', false),
  createVersionRegardlessOfUpdateControl: getPermission('item/createVersionRegardlessOfUpdateControl', true),
  createPlaceholder: getPermission('item/createPlaceholder', false),
  restoreMedia: getPermission('item/restoreMedia', false),
  downloadMarkers: getPermission('item/downloadMarkers', false),
  editRestrictions: getPermission('item/editRestrictions', false),
  sendToStorage: getPermission('item/sendToStorage', true),
  downloadMedia: getPermission('item/downloadMedia', true),

  createCloudLive: getPermission('item/createCloudLive', false),
  createCloudLiveInExistingPlaceholder: getPermission('item/createCloudLiveInExistingPlaceholder', false),
  chooseUploadLocation: getPermission('item/chooseUploadLocation', true),
  history: getPermission('item/history', true),
  changeOwner: getPermission('item/changeOwner', false),
  move: getPermission('item/move', true),
  reprocess: getPermission('item/reprocess', false),
  commentRead: getPermission('item/commentRead', true),
  commentWrite: getPermission('item/commentWrite', true),
  commentStatus: getPermission('item/commentStatus', true),
  commentDeleteOwn: getPermission('item/commentDeleteOwn', true),
  commentDeleteOthers: getPermission('item/commentDeleteOthers', false),
};

export const TaskPermissions = {
  listAll: getPermission('task/listAll', false),
};


/**
 * Retrieves new permissions that are universally rolled out to all tenants.
 * Note: Include new rollout permission only if the default value is `true`.
 *
 * @returns All permissions that are needed to be rolled out to all tenants.
 */
export const getAllRollOutPermission = (existingPermissions: string[]) => {
  const rollOutPermissions: string[] = [];
  /*
    Add all new rollout permissions here...
    e.g: https://github.com/Mjoll/mimir/commit/7b207982977e4997a085c0954ea01599e64f5b4d#diff-2391dcab4ad55233cdb1b924ed8738fd7f83e1bfebf0e48cd2c139a96a3b2834R126-R147
    details: check "what was done" https://github.com/Mjoll/mimir/pull/11168
  */

  return rollOutPermissions;
};

export const PersonPermissions = {
  create: getPermission('person/create', true),
  delete: getPermission('person/delete', true),
  update: getPermission('person/update', true),
  merge: getPermission('person/merge', true),
};

export const FolderPermissions = {
  create: getPermission('folder/create', true),
  delete: getPermission('folder/delete', true),
  update: getPermission('folder/update', true),
  changeOwner: getPermission('folder/changeOwner', false),
};

export const SharePermissions = {
  shareMedia: getPermission('share/shareMedia', false),
  requestUpload: getPermission('share/requestUpload', false),
};

/**
 * This object has the new permissions that we introduce as keys that should
 * have default value from the current value of another old permission as values.
 * for example: when we are splitting a permission or introducing new permissions.
 */
export const newPermissionsDefaultValueFromOldPermissions: Record<string, string[]> = {
  [ItemPermissions.copyMedia.permission]: [ItemPermissions.update.permission],
  [ItemPermissions.updateMetadata.permission]: [ItemPermissions.update.permission],
  [ItemPermissions.promote.permission]: [ItemPermissions.version.permission],
  [ItemPermissions.unarchive.permission]: [ItemPermissions.archive.permission],
  [ItemPermissions.upload.permission]: [ItemPermissions.create.permission],
  [ItemPermissions.exportSequence.permission]: [ItemPermissions.create.permission],
  [ItemPermissions.dependenciesOnExportSequence.permission]: [ItemPermissions.create.permission],
  [ItemPermissions.recoverDeleted.permission] : [ItemPermissions.delete.permission],
  [ItemPermissions.move.permission]: [FolderPermissions.update.permission],
  [ItemPermissions.commentWrite.permission]: [ItemPermissions.update.permission],
  [ItemPermissions.commentStatus.permission]: [ItemPermissions.update.permission],
  [ItemPermissions.commentDeleteOwn.permission]: [ItemPermissions.commentWrite.permission],

  [GeneralPermissions.mimirCutter.permission]: [ItemPermissions.create.permission],
  [UIOnlyPermissions.cutter.permission]: [GeneralPermissions.mimirCutter.permission],
  [GeneralPermissions.getGroups.permission]: [GeneralPermissions.bookmarks.permission, GeneralPermissions.setVisibleToGroups.permission],
};

/**
 * This allow to inherit the new permission default value form the old permission value.
 * For example: If we split old 'P1', into 'S1', 'S2' two new permissions,
 * Then set new 'S1', 'S2' values whatever default of 'P1' has.
 */
export const inheritDefaultValueFromOldPermissions = (allPermissionDefaultMap: Record<string, boolean>, oldPermissions: Record<string, boolean>) => {
  if (Object.keys(oldPermissions).length) {
    for (const [newPermission, oldPermissionList] of Object.entries(newPermissionsDefaultValueFromOldPermissions)) {
      allPermissionDefaultMap[newPermission] = oldPermissionList.some(p => oldPermissions[p]);
    }
  }
};

export const getAllPermissionsWithDefaultValues = (classified: boolean = false): Record<string, boolean> => {
  const permissions: Record<string, Permission>[] = [
    GeneralPermissions,
    AnalyticsPermissions,
    UserManagementPermissions,
    SystemActivityPermissions,
    ItemPermissions,
    PersonPermissions,
    FolderPermissions,
    SharePermissions,
    TaskPermissions,
    UIOnlyPermissions,
  ];

  if (classified) {
    permissions.push(ClassifiedUserManagementPermissions);
    permissions.push(ClassifiedAllTenantsPermissions);
  }

  const result: Record<string, boolean> = {};
  for (const permission of permissions) {
    for (const value of Object.values(permission)) {
      result[value.permission] = value.defaultValue;
    }
  }
  return result;
};

export const getAllPermissions = (classified: boolean = false) =>
  Object.keys(getAllPermissionsWithDefaultValues(classified));

export const SystemGroups = {
  superAdmin: {
    id: 'super-admin',
    name: 'Super Administrator',
    permission: ClassifiedUserManagementPermissions.superAdmin.permission,
  },
  tenantAdmin: {
    // Each tenant has different tenant group, hence different id
    name: 'Administrator',
    permission: ClassifiedUserManagementPermissions.tenantAdmin.permission,
  },
  supportTeam: {
    id: 'support-team',
    name: 'Support Team',
    permission: ClassifiedUserManagementPermissions.supportTeam.permission,
  },
  defaultGroup: {
    // Each tenant has different default group with different id & name
  },
};

export const Roles = {
  superAdmin: SystemGroups.superAdmin.permission,
  tenantAdmin: SystemGroups.tenantAdmin.permission,
  supportTeam: SystemGroups.supportTeam.permission,
};

export const SuperAdminGroupId = SystemGroups.superAdmin.id;
const SuperAdminGroupName = SystemGroups.superAdmin.name;

export const SupportTeamGroupId = SystemGroups.supportTeam.id;
const SupportTeamGroupName = SystemGroups.supportTeam.name;

export const TenantAdminGroupName = SystemGroups.tenantAdmin.name;

export const SuperAdminGroupIds = [SuperAdminGroupId, SupportTeamGroupId];
export const StaticGroupNames = [
  SuperAdminGroupName,
  TenantAdminGroupName,
  SupportTeamGroupName,
];

/**
 * This contains permissions which are exclusively for Super Admins.
 * Backend and client checks for these permissions for different purposes, like:
 * To check for super admin permissions while updating/creating a group
 * Permission tree checks and excludes these permissions.
 */
export const SuperAdminOnlyPermissions = [
  SystemGroups.superAdmin.permission,
];

/**
 * Permissions that should be associated with Support Team
 */
export const SupportTeamPermissions: string[] = [
];

/**
 * prerequisite to dependent permission mappings.
 * Have multiple pre-requisite permissions.
 * e.g. - 'dependenciesOnExportSequence' requires both 'create' & 'exportSequence' permission.
 */
export const prerequisiteToDependentPermissions = {
  // item create dependent permissions
  [ItemPermissions.create.permission]: [
    ItemPermissions.upload.permission,
    ItemPermissions.exportSequence.permission,
    ItemPermissions.dependenciesOnExportSequence.permission,
    GeneralPermissions.mimirCutter.permission,
  ],

  // To create or update comments, you must be allowed to see them
  [ItemPermissions.commentRead.permission]: [
    ItemPermissions.commentWrite.permission,
    ItemPermissions.commentStatus.permission,
    ItemPermissions.commentDeleteOwn.permission,
  ],

  // item exportSequence dependent permissions
  [ItemPermissions.exportSequence.permission]: [ItemPermissions.dependenciesOnExportSequence.permission],

  // item update dependent permissions
  [ItemPermissions.update.permission]: [
    ItemPermissions.updateTranscript.permission,
    SharePermissions.requestUpload.permission
  ],

  // item upload dependent permissions
  [ItemPermissions.upload.permission]: [SharePermissions.requestUpload.permission],

  // get groups dependent permissions
  [GeneralPermissions.getGroups.permission]: [
    GeneralPermissions.bookmarks.permission,
    GeneralPermissions.setVisibleToGroups.permission,
  ],

  [ItemPermissions.createCloudLive.permission]: [
    ItemPermissions.createCloudLiveInExistingPlaceholder.permission
  ],
  [GeneralPermissions.mimirCutter.permission]: [UIOnlyPermissions.cutter.permission]

};

/**
 * dependent to prerequisite permission mappings.
 * does have multiple pre-requisite permissions.
 */
export const dependentToPrerequisitePermissions = Object.entries(prerequisiteToDependentPermissions).reduce<Record<string, string[]>>(
  (mapping, entry) => {
    const [prerequisite, dependents] = entry;
    for (const dependent of dependents) {
      if (!mapping[dependent]) mapping[dependent] = [];

      mapping[dependent].push(prerequisite);
    }
    return mapping;
  }, {}
);

export const fetchlogsPermissions = [
  SystemActivityPermissions.download,
  SystemActivityPermissions.upload,
  SystemActivityPermissions.processor,
  GeneralPermissions.setVisibleToSelf,
  ItemPermissions.upload,
  ItemPermissions.createPlaceholder,
  ItemPermissions.create,
  ItemPermissions.update,
  SharePermissions.requestUpload,
];
